import { useGetResidenceStatusList } from "@/api/services/global/master";
import { UploadResidenceCardFileBtn } from "@/components/commons/UploadResidenceCardFileBtn";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { TEmployeeForm } from "@/features/employee/types/employeeForm";
import { useScrollToElement } from "@/hooks/useScrollToElement";
import { text } from "@/theme/colors";
import { EMPLOYMENT_STATUS, RESIDENCE_STATUS_FOR_COE_NAME } from "@/types/enum";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

type TResidentFormProps = {};

export const ResidentForm: FC<TResidentFormProps> = () => {
  const { control, setValue } = useFormContext<TEmployeeForm>();

  const residenceStatusIdWatchField = useWatch({ control, name: "residenceStatusId" });
  const employmentStatusWatchField = useWatch({ control, name: "employmentStatus" });
  const provinceWatchField = useWatch({ control, name: "province" });
  const municipalityWatchField = useWatch({ control, name: "municipality" });
  const addressWatchField = useWatch({ control, name: "address" });
  const firstNameWatchField = useWatch({ control, name: "firstName" });
  const residenceExpiryDateWatchField = useWatch({ control, name: "residenceExpiryDate" });

  const {
    data: residenceStatus,
    isLoading: isLoadingResidenceStatus,
    residenceStatusOptions,
    residenceStatusForCoeOptions,
  } = useGetResidenceStatusList();

  const residenceStatusOptionsWithFilter = useMemo(() => {
    const options = employmentStatusWatchField === EMPLOYMENT_STATUS.OFFERED ? residenceStatusForCoeOptions : residenceStatusOptions;
    return options;
  }, [employmentStatusWatchField, residenceStatusForCoeOptions, residenceStatusOptions]);

  const residencePeriodOptions = useMemo(() => {
    const founded = residenceStatus ? residenceStatus.find((item) => toNumber(item.id) === toNumber(residenceStatusIdWatchField)) : undefined;
    if (founded) return founded.residencePeriods.map((item) => ({ label: item.residencePeriod ?? "", value: item.id, hidden: item.isHidden }));
    else return [];
  }, [residenceStatus, residenceStatusIdWatchField]);

  const isResidenceStatusForCoeName = useMemo(
    () => findLabelByValue(residenceStatusForCoeOptions, residenceStatusIdWatchField) === RESIDENCE_STATUS_FOR_COE_NAME,
    [residenceStatusForCoeOptions, residenceStatusIdWatchField],
  );

  const handleChangeResidenceStatus = async (value: string) => {
    if (findLabelByValue(residenceStatusForCoeOptions, value) === RESIDENCE_STATUS_FOR_COE_NAME) {
      setValue("residenceCode", null);
      setValue("residenceExpiryDate", null);
      setValue("residenceCardFrontFile", null);
      setValue("residenceCardBackFile", null);
    }
    setValue("residenceStatusId", value as string);
    setValue("residencePeriodId", null);
    setValue("contractSpecificBusinessClassificationOneId", "");
    setValue("contractSpecificBusinessClassificationTwoId", null);
    setValue("contractSpecificBusinessClassificationThreeId", null);
  };

  const { elmId: residenceCodeElmId } = useScrollToElement("residence_code_input");

  return (
    <Stack gap={1}>
      <InnerBox title="在留カード情報">
        <Grid container rowSpacing={2} columnSpacing={3}>
          <Grid item xs={4}>
            <FormField id={residenceCodeElmId} control={control} name="residenceCode">
              <Input disabled={isResidenceStatusForCoeName} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="residenceStatusId">
              <Select
                value={residenceStatusIdWatchField}
                loading={isLoadingResidenceStatus}
                options={residenceStatusOptionsWithFilter}
                onChange={handleChangeResidenceStatus}
              />
            </FormField>
          </Grid>
          <Grid item xs={4} />
          <Grid item xs={4}>
            <FormField control={control} name="residencePeriodId">
              <Select options={residencePeriodOptions} disabled={isResidenceStatusForCoeName} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="residenceExpiryDate">
              <InputDatePicker disabled={isResidenceStatusForCoeName} />
            </FormField>
          </Grid>
          <Grid item xs={4} />
          <Grid item xs={4}>
            <FormField control={control} name="residenceCardFrontFile">
              <UploadResidenceCardFileBtn
                fieldLabel="在留カード写し表データ"
                readonly={isResidenceStatusForCoeName}
                residenceExpiryDate={residenceExpiryDateWatchField ? residenceExpiryDateWatchField.format("YYYYMMDD") : "9999999"}
                employeeFirstName={firstNameWatchField}
              />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="residenceCardBackFile">
              <UploadResidenceCardFileBtn
                fieldLabel="在留カード写し裏データ"
                readonly={isResidenceStatusForCoeName}
                residenceExpiryDate={residenceExpiryDateWatchField ? residenceExpiryDateWatchField.format("YYYYMMDD") : "9999999"}
                employeeFirstName={firstNameWatchField}
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="cap12" component="p" color={text.secondary}>
              在留カードに記載された住所が最新及び以下の住所と一致していることをご確認ください。
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="cap12" component="p" mb={0.5}>
              住所
            </Typography>
            <Input value={(provinceWatchField || "") + (municipalityWatchField || "") + (addressWatchField || "")} readonly sx={{ width: "100%" }} />
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};
